import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Checkbox } from 'primereact/checkbox';
import { ErrorMessage } from 'formik';
import gql from 'graphql-tag';
import apollo from '../services/apollo';

const GET_PUBLIC_DOCUMENT_URL_QUERY = gql`
  query getPublicDocumentUrlQuery($type: DocumentType!) {
    publicDocumentUrl(type: $type) {
      url
    }
  }
`;

const getDocumentUrl = async (documentType) => {
  try {
    apollo
      .query({
        query: GET_PUBLIC_DOCUMENT_URL_QUERY,
        variables: {
          type: documentType,
        },
      })
      .then((res) => {
        const documentUrl = res.data.publicDocumentUrl.url;

        window.open(documentUrl, '_blank', 'noreferrer');
      });
  } catch (error) {
    console.log(error);
  }
};

const messages = defineMessages({
  termsOfUse: { id: 'msg.termsOfUse', defaultMessage: 'Terms of Use' },
  privacyPolicy: { id: 'msg.privacyPolicy', defaultMessage: 'Privacy Policy' },
  clientTermsOfUse: { id: 'msg.clientTermsOfUse', defaultMessage: 'Terms of Use' },
  termsOfService: { id: 'msg.termsOfService', defaultMessage: 'Terms of Service' },
  gpAgreement: {
    id: 'msg.gpAgreement',
    defaultMessage: 'GP Agreement',
  },
});

export const GpInvoiceAddressConfirmationCheckbox = ({
  field,
  form: { touched, errors },
  ownerInvoiceCountryDisplayName,
  ...props
}) => {
  const displayError = touched[field.name] && errors[field.name];

  return (
    <>
      <Checkbox
        {...field}
        {...props}
        checked={field.value}
        onChange={field.onChange}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      <FormattedMessage
        id="msg.gp_invoice_address_confirmation"
        defaultMessage="I confirm that the above invoice address is in {ownerInvoiceCountryDisplayName}."
        values={{
          ownerInvoiceCountryDisplayName,
        }}
      />
    </>
  );
};

export const UserAgreementCheckbox = ({ field, form: { touched, errors }, labelText, ...props }) => {
  const { formatMessage } = useIntl();

  const displayError = touched[field.name] && errors[field.name];

  return (
    <>
      <Checkbox
        {...field}
        {...props}
        checked={field.value}
        onChange={field.onChange}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      <FormattedMessage
        id="msg.term_and_policy_agreement"
        defaultMessage="I have read and accepted the {termsOfUseLink} and {privacyPolicyLink}."
        values={{
          termsOfUseLink: (
            <button type="button" className="link" onClick={() => getDocumentUrl('USER_TERMS_OF_USE')}>
              {formatMessage(messages.termsOfUse)}
            </button>
          ),
          privacyPolicyLink: (
            <button type="button" className="link" onClick={() => getDocumentUrl('PRIVACY_POLICY')}>
              {formatMessage(messages.privacyPolicy)}
            </button>
          ),
        }}
      />
    </>
  );
};

UserAgreementCheckbox.propTypes = {
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export const ClientAgreementCheckbox = ({
  field,
  form: { touched, errors },
  labelText,
  invitationIsForGp,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const displayError = touched[field.name] && errors[field.name];

  const termsOfUse = invitationIsForGp ? (
    <FormattedMessage
      id="msg.client_terms_of_use_and_gp_agreement"
      defaultMessage="I have read and accept the {clientTermsOfUseLink} and {gpAgreementLink}"
      values={{
        clientTermsOfUseLink: (
          <button type="button" className="link" onClick={() => getDocumentUrl('OWNER_TERMS_OF_USE')}>
            {formatMessage(messages.clientTermsOfUse)}
          </button>
        ),
        gpAgreementLink: (
          <button type="button" className="link" onClick={() => getDocumentUrl('GPI_PARTNER_AGREEMENT')}>
            {formatMessage(messages.gpAgreement)}
          </button>
        ),
      }}
    />
  ) : (
    <FormattedMessage
      id="msg.client_terms_of_use_agreement"
      defaultMessage="I have read and accept the {clientTermsOfUseLink}"
      values={{
        clientTermsOfUseLink: (
          <button type="button" className="link" onClick={() => getDocumentUrl('OWNER_TERMS_OF_USE')}>
            {formatMessage(messages.clientTermsOfUse)}
          </button>
        ),
      }}
    />
  );

  return (
    <>
      <Checkbox
        {...field}
        {...props}
        checked={field.value}
        onChange={field.onChange}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      {termsOfUse}
    </>
  );
};

ClientAgreementCheckbox.propTypes = {
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
};

export const BankTransferTermsOfUseCheckbox = ({ field, form: { touched, errors }, labelText, ...props }) => {
  const { formatMessage } = useIntl();

  const displayError = touched[field.name] && errors[field.name];

  return (
    <>
      <Checkbox
        {...field}
        {...props}
        checked={field.value}
        onChange={field.onChange}
        className={displayError ? 'p-error' : ''}
        style={{ marginRight: '.25em' }}
      />
      <FormattedMessage
        id="msg.bank_transfer_terms_of_use_agreement"
        defaultMessage="I accept the {clientTermsOfUseLink} and an administration fee of $25 per invoice."
        values={{
          clientTermsOfUseLink: (
            <button type="button" className="link" onClick={() => getDocumentUrl('OWNER_TERMS_OF_USE')}>
              {formatMessage(messages.termsOfService)}
            </button>
          ),
        }}
      />
      <ErrorMessage name={field.name} component="div" />
    </>
  );
};

BankTransferTermsOfUseCheckbox.propTypes = {
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  labelText: PropTypes.string.isRequired,
};
